import axios from 'axios';
import Config from '../../Config';

class CampaignsRequest {
  static #instance = null;
  #baseURL = `${Config.backendDomain}vendor`;
  #guestURL = `${Config.backendDomain}`;
  #imageProcessingQueue = [];
  #processingPromise = null;

  static getInstance() {
    if (!this.#instance) {
      this.#instance = new CampaignsRequest();
    }
    return this.#instance;
  }

  /**
   *Todo => Converts an image file or URL to base64
   */
  async #convertImageToBase64(image) {
    // If image is already a base64 string, return it
    if (typeof image === 'string' && image.startsWith('data:image')) {
      return image;
    }

    // If image is a File object
    if (image instanceof File) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(image);
      });
    }
    if (typeof image === 'string' && image.startsWith('https')) {
      return image;
    }
    if (typeof image === 'string' && image.startsWith('http')) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/jpeg', 0.92));
        };
        img.onerror = () => reject(new Error('Failed to load external image'));
        img.src = image;
      });
    }

    throw new Error('Invalid image format');
  }

  /**
   *Todo => Validates and processes campaign data
   */
  #validateAndProcessData(formData) {
    const requiredFields = [
      'title',
      'category',
      'redemption_type',
      'phone_number',
      'start_date',
      'expiry_date',
      'terms_and_conditions',
      'redemption_instructions',
      'refund_policy',
      'description',
      'sms_content'
    ];

    const missingFields = requiredFields.filter(field => !formData[field]);

    if (missingFields.length > 0) {
      throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
    }

    // Check if price is provided but discounted_price is missing
    if (formData.price && !formData.discounted_price) {
      throw new Error('Discounted price is required when price is provided');
    }

    // Use the formatted locations directly from the form data
    // This ensures we use the already processed location data
    const locations = formData.location || formData.region_location.map(loc => ({
      region: loc.region,
      location: loc.location === "other" ? loc.customLocation : loc.location
    }));

    // Format dates with time component
    const formatDateTime = (date) => {
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
    };

    // Construct the payload in the expected format
    const processedData = {
      title: formData.title,
      category: formData.category,
      redemption_type: formData.redemption_type,
      phone_number: formData.phone_number,
      description: formData.description,
      price: formData.price ? Number(formData.price).toFixed(2) : null,
      discounted_price: formData.discounted_price ? Number(formData.discounted_price).toFixed(2) : null,
      location: locations,
      start_date: formatDateTime(formData.start_date),
      end_date: formatDateTime(formData.expiry_date),
      sms_content: formData.sms_content,
      redemption_instructions: formData.redemption_instructions,
      refund_policy: formData.refund_policy,
      terms_and_conditions: formData.terms_and_conditions
    };

    if (processedData.price && processedData.discounted_price) {
      if (parseFloat(processedData.discounted_price) >= parseFloat(processedData.price)) {
        throw new Error('Discounted price must be less than original price');
      }
    }

    return processedData;
  }

  /**
   *Todo => Creates or updates a campaign
   */
  async createOrUpdateCampaign(campaignData, image, slug = null, slug_type = null) {
    try {
      //* Validating and processing data
      const processedData = this.#validateAndProcessData(campaignData);

      //* Converting image to base64 with retry mechanism
      let base64Image = null;
      if (image) {
        const maxRetries = 3;
        for (let i = 0; i < maxRetries; i++) {
          try {
            base64Image = await this.#convertImageToBase64(image);
            break;
          } catch (error) {
            if (i === maxRetries - 1) throw error;
            await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
          }
        }
      }

      //* Constructing final payload
      const payload = {
        ...processedData,
        image: base64Image
      };

      //* Making API request with authorization header
      const headers = {
        Authorization: `Bearer ${localStorage.getItem('dealboxx_token')}`
      };

      const response = (slug && slug_type === "update")
        ? await axios.patch(`${this.#baseURL}/campaign/${slug}`, payload, { headers })
        : await axios.post(`${this.#baseURL}/campaign`, payload, { headers });

      return {
        success: true,
        data: response.data,
        message: `Campaign ${slug ? 'updated' : 'created'} successfully`
      };

    } catch (error) {
      const errorMessage = error.response?.data?.message 
        || error.message 
        || 'Failed to process campaign';

      console.error('Campaign Request Error:', {
        message: errorMessage,
        stack: error.stack,
        details: error.response?.data
      });

      return {
        success: false,
        error: errorMessage,
        details: error.response?.data || null
      };
    }
  }

  /**
   *Todo => Deletes a campaign
   */
  async deleteCampaign(slug) {
    try {
      const response = await axios.delete(`${this.#baseURL}/campaign/${slug}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('dealboxx_token')}`
        }
      });
      return {
        success: true,
        message: 'Campaign deleted successfully',
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete campaign',
      };
    }
  }
  async getCampaignById(slug){
    try {
      const response = await axios.get(`${this.#baseURL}/campaign/${slug}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('dealboxx_token')}`
        }
      });
      return response.data.data;
    } catch (error) {
      return null;
    }
  }


  //Todo => Blast Campaign
  async blastCampaign(senderId, quantity, campaign_slug) {
    try {
      const response = await axios.post(`${this.#baseURL}/blast/deal/${campaign_slug}`, {
        sender_id: senderId,
        quantity: quantity,
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('dealboxx_token')}`
        }
      });
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to blast campaign',
      };
    }
  }

  //Todo => Get Campaign Redemptions
  async getCampaignRedemptions(){
    try {
      const response = await axios.get(`${this.#baseURL}/redeemed/campaigns`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('dealboxx_token')}`
        }
      });
      return response.data;
    } catch (error) {
      return null;
    }
  }

  //Todo => Complete Redemption
  async completeRedemption(campaignSlug){
    try {
      const response = await axios.patch(`${this.#baseURL}/update/campaign/completed/${campaignSlug}`, {},{
        headers: {
          Authorization: `Bearer ${localStorage.getItem('dealboxx_token')}`
        }
      });
      return response.data;
    } catch (error) {
      return null;
    }
  }

  //Todo => Fetch CampaignCategoriesCount
  async campaignCategorySubscribersStats(){
    try{
      const response = await axios.get(`${this.#guestURL}subscribe/stats/category`);
      return response?.data;
    }catch(error){
      return null
    }
  }

  //Todo => Get Stats by network
  async getStatsByNetwork(){
    try{
      const response = await axios.get(`${this.#guestURL}subscribe/stats/network`);
      return response?.data;
    }catch(error){
      return null
    }
  }

  //Todo => Get Recent Campaigns
  async getRecentCampaigns(number){
    try{
      const response = await axios.get(`${this.#baseURL}/vendorCampaigns/${number}`,{
        headers: {
          Authorization: `Bearer ${localStorage.getItem('dealboxx_token')}`
        }
      });
      return response?.data;
    }catch(error){
      return null
    }
  }

  //Todo => Get Subscribers Distribution by Region
  async getSubscribersDistributionByRegion(){
    try{
      const response = await axios.get(`${this.#guestURL}subscribe/stats/region`);
      return response?.data;
    }catch(error){
      return null
    }
  }

  //Todo => SenD Test Campaign to five numbers
  async sendTestCampaign(campaignSlug, data){
      try{
        const response = await axios.post(`${this.#baseURL}/test/blast/campaign/${campaignSlug}`, data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('dealboxx_token')}`
        }
      });
      return response?.data;
    }catch(error){
      return null
    }
  }

  //Todo => Reset Blast Count
  async resetBlastCount(campaignSlug) {
    try {
      const response = await axios.get(`${this.#baseURL}/reset_deal_blast/${campaignSlug}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('dealboxx_token')}`
        }
      });
      return {
        status:true,
        data:response?.data?.data,
        message:response?.data?.reason || 'Blast count reset successfully',
      }
    } catch (error) {
      return {
        status:false,
        data:null,
        message:error.response?.data?.reason || 'Failed to reset blast count',
      };
    }
  }
}


//* Export singleton instance
export const campaignsRequest = CampaignsRequest.getInstance();
